import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import UserAvatar from 'components/UserAvatar';
import CustomDialog from 'components/CustomDialog';
import { getPublicProfile, getPublicSpotsByUser } from 'store/slices/publicProfile';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { alpha, Box, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { styled } from 'theme';
import SpotPreview, { SpotOptions } from '../SpotPreview';
import { SocialNetworksBox } from '../SocialNetworksBox';
import Grid from '@mui/material/Unstable_Grid2';

interface UserDetailParams {
    id: string;
}

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: 'center',
    color: theme.palette.text.secondary,
}));

const UserDetail = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const dispatch = useAppDispatch();
    const { id } = useParams<UserDetailParams>();
    const publicProfile = useAppSelector((state) => state.publicProfile);

    const {
        getPublicProfilePending,
        getPublicProfileSuccess,
        getPublicProfileError,
        getPublicProfileData,
        getPublicSpotsByUserPending,
        getPublicSpotsByUserData,
    } = publicProfile;

    useEffect(() => {
        void dispatch(getPublicProfile({ idUser: id }));
    }, [dispatch, id]);

    useEffect(() => {
        if (!getPublicProfilePending && getPublicProfileSuccess) {
            void dispatch(getPublicSpotsByUser({ idUser: id }));
        }
    }, [dispatch, id, getPublicProfilePending, getPublicProfileSuccess]);

    return (
        <CustomDialog
            processing={getPublicProfilePending}
            processingCopy={'Loading profile...'}
            success={getPublicProfileSuccess}
            successUsesMain
            error={getPublicProfileError}
            errorContent={
                <>
                    <Typography variant={'subtitle1'}>There was an error getting the profile</Typography>
                    <Typography variant={'body1'}>Please try again later.</Typography>
                </>
            }
        >
            <Box>
                {getPublicProfileData ? <>
                    <Typography variant={'h2'} align={'center'} mb={2}>
                        {`${getPublicProfileData.firstname ?? ''} ${getPublicProfileData.lastname ?? ''}`.trim()}
                    </Typography>
                    <Grid
                        container
                        spacing={3}
                        direction={isMobile ? 'column' : 'row'}
                        alignItems="center"
                        justifyContent="center"
                        sx={{ height: isMobile ? 'auto' : '100%' }}
                    >
                        <Grid xs={12} sm={6}>
                            <Box
                                my={2}
                                display="flex"
                                justifyContent="center"
                                sx={{
                                    height: '100%',
                                }}
                            >
                                <UserAvatar
                                    sx={{
                                        width: '160px',
                                        height: '160px',
                                    }}
                                    src={getPublicProfileData.picture?.medium?.url}
                                />
                            </Box>
                        </Grid>
                        <Grid
                            xs={12}
                            sm={6}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {getPublicProfileData?.customSocialNetworks && getPublicProfileData?.availableSocialNetworks ? (
                                <>
                                    <SocialNetworksBox
                                        customSocialNetworks={getPublicProfileData?.customSocialNetworks}
                                        availableSocialNetworks={getPublicProfileData?.availableSocialNetworks}
                                    />
                                </>
                            ) : null}
                        </Grid>
                    </Grid>
                    {/* eslint-disable-next-line @typescript-eslint/no-unsafe-member-access */}
                    {getPublicProfileData?.stats && (
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                        <Box display="flex" justifyContent="center" gap={4} mt={2} mb={1}>
                            <Box textAlign="center">
                                {/* eslint-disable-next-line @typescript-eslint/no-unsafe-member-access */}
                                <Typography variant="h5">{(getPublicProfileData.stats as { spotsCount: number }).spotsCount}</Typography>
                                <Typography variant="caption" color="text.secondary">spots</Typography>
                            </Box>
                            <Box textAlign="center">
                                {/* eslint-disable-next-line @typescript-eslint/no-unsafe-member-access */}
                                <Typography variant="h5">{(getPublicProfileData.stats as { totalLikesReceived: number }).totalLikesReceived}</Typography>
                                <Typography variant="caption" color="text.secondary">likes received</Typography>
                            </Box>
                            <Box textAlign="center">
                                {/* eslint-disable-next-line @typescript-eslint/no-unsafe-member-access */}
                                <Typography variant="h5">{(getPublicProfileData.stats as { totalCheckinsReceived: number }).totalCheckinsReceived}</Typography>
                                <Typography variant="caption" color="text.secondary">fly-throughs</Typography>
                            </Box>
                        </Box>
                    )}
                </> : null}
                {getPublicSpotsByUserPending ? (
                    <Typography variant={'h5'} mt={3}>
                        Loading spots...
                    </Typography>
                ) : getPublicSpotsByUserData && getPublicSpotsByUserData.length > 0 ? (
                    <>
                        <Typography variant={'h4'} my={3}>
                            Latest spots from {getPublicProfileData?.firstname}
                        </Typography>
                        <Stack direction='row' justifyContent='center' alignItems='center' spacing={1}>
                            {getPublicSpotsByUserData.map((spot) => (
                                <Item
                                    key={spot.id}
                                    sx={{
                                        alignSelf: 'stretch',
                                        display: 'flex',
                                        py: 4,
                                        px: 2,
                                        border: `1px solid ${alpha(theme.palette.secondary.main, 0.7)}`,
                                    }}
                                >
                                    <Grid
                                        container
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <Grid
                                            xs={12}
                                            sx={{
                                                flexGrow: 1,
                                                mb: 2,
                                            }}
                                        >
                                            <SpotPreview data={spot}/>
                                        </Grid>
                                        <Grid xs={12}>
                                            <SpotOptions data={spot}/>
                                        </Grid>
                                    </Grid>
                                </Item>
                            ))}
                        </Stack>
                    </>
                ) : (
                    <Typography variant={'h5'} mt={3}>No public spots found for this user.</Typography>
                )}
            </Box>
        </CustomDialog>
    );
};

export default UserDetail;
